import PropTypes from 'prop-types';
import {useMemo} from 'react';

import {Box, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';
import {IconSetSectionContext} from '@amblique/king-living/app/contentful/contexts/icon-set-section-context';
import {clampIconSetDesktopColumns} from '@amblique/king-living/app/theme/contentful/icon-set';
import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const ICONS_WRAPPER_PADDING = {
  paddingTop: {base: '80px', md: '80px'},
  paddingBottom: {base: '80px', md: '80px'},
  paddingLeft: {base: '20px', md: '20px'},
  paddingRight: {base: '20px', md: '20px'},
};

function filterItems(items) {
  return (items ?? []).filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  );
}

/**
 * @override
 * IconSet (CF-054): explainerBlocksCollection is the primary item source; iconsCollection remains for legacy entries.
 * When both are filled, only explainer blocks render. Desktop column count comes from maxItemsPerRowDesktop (default 3).
 *
 * @param {Object} props - Component props
 * @param {Contentful.IconSet} props.component - IconSet component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const IconSet = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const icons = useMemo(
    () => filterItems(component?.iconsCollection?.items),
    [component?.iconsCollection?.items]
  );
  const explainerBlocks = useMemo(
    () => filterItems(component?.explainerBlocksCollection?.items),
    [component?.explainerBlocksCollection?.items]
  );
  const useExplainer = explainerBlocks.length > 0;
  const items = useExplainer ? explainerBlocks : icons;
  const hasItems = items.length > 0;
  const desktopColumns = clampIconSetDesktopColumns(component?.maxItemsPerRowDesktop);
  const lastRowCount = hasItems ? (items.length % desktopColumns || desktopColumns) : 0;
  const lastRowIsOdd = lastRowCount > 0 && lastRowCount % 2 === 1;

  const styleProps = {
    component,
    isComplete,
    isFetching,
    lastRowIsOdd,
  };
  const stylesIcons = useMultiStyleConfig('contentful/icon-set', {
    ...styleProps,
    variant: 'icons',
  });
  const stylesValueProp = useMultiStyleConfig('contentful/icon-set', {
    ...styleProps,
    variant: 'valueProp',
  });

  const iconsWrapperStyles = useMemo(
    () => ({
      width: '100%',
      backgroundColor: normalizeHex(component?.backgroundColour) ?? 'white',
      color: normalizeHex(component?.textColour) || undefined,
      ...ICONS_WRAPPER_PADDING,
    }),
    [component?.backgroundColour, component?.textColour]
  );

  const skeletonItems = [1, 2, 3];

  if (!isComplete && isFetching) {
    return (
      <Box {...stylesValueProp.root}>
        {skeletonItems.map((index) => (
          <Box key={`skeleton-${index}`} {...stylesValueProp.icon}>
            <Skeleton height="64px" width="64px" />
            <Skeleton height="16px" width="80px" />
            <Skeleton height="14px" width="120px" />
          </Box>
        ))}
      </Box>
    );
  }

  if (!hasItems) {
    return null;
  }

  const styles = useExplainer ? stylesValueProp : stylesIcons;
  const outerBoxProps = useExplainer ? styles.wrapper : iconsWrapperStyles;

  const rootClassName = [
    className,
    'icon-set',
    useExplainer ? 'icon-set--value-prop' : 'icon-set--logos',
  ].filter(Boolean).join(' ');
  const sectionTitle = component?.title?.trim();
  const sectionContext = useExplainer ? 'valueProp' : 'icons';

  return (
    <Box as="div" {...props} className={rootClassName}>
      <IconSetSectionContext.Provider value={sectionContext}>
        <Box {...outerBoxProps} className="icon-set__wrapper">
          {sectionTitle ? (
            <Text {...styles.title} className="icon-set__title">
              {sectionTitle}
            </Text>
          ) : null}
          <Box {...styles.root} className="icon-set__grid">
            {items.map((item) => (
              <Box key={item.sys.id} {...styles.icon} className="icon-set__item">
                <ContentfulComponent component={item} />
              </Box>
            ))}
          </Box>
        </Box>
      </IconSetSectionContext.Provider>
    </Box>
  );
};

IconSet.displayName = 'IconSet';
IconSet.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
